import React, { useMemo, useState } from "react";
import { CATEGORY_LABELS, CATEGORY_ORDER, IShowcaseEntry, ShowcaseCategory, SHOWCASE_REGISTRY } from "../_registry";

export interface ISidebarNavProps {
  activeSlug: string;
  onSelect: (slug: string) => void;
}

interface IGroupedEntries {
  category: ShowcaseCategory;
  entries: IShowcaseEntry[];
}

export const SidebarNav: React.FC<ISidebarNavProps> = ({ activeSlug, onSelect }: ISidebarNavProps): JSX.Element => {
  const grouped: IGroupedEntries[] = useMemo(() => {
    return CATEGORY_ORDER.map((category: ShowcaseCategory): IGroupedEntries => ({
      category,
      entries: SHOWCASE_REGISTRY.filter((entry: IShowcaseEntry): boolean => entry.category === category)
    })).filter((group: IGroupedEntries): boolean => group.entries.length > 0);
  }, []);

  const activeCategory: ShowcaseCategory | undefined = useMemo(() => {
    const found: IShowcaseEntry | undefined = SHOWCASE_REGISTRY.find(
      (entry: IShowcaseEntry): boolean => entry.slug === activeSlug
    );
    return found?.category;
  }, [activeSlug]);

  const [collapsed, setCollapsed] = useState<Set<ShowcaseCategory>>(() => new Set());

  const toggleCategory = (category: ShowcaseCategory): void => {
    setCollapsed((prev: Set<ShowcaseCategory>): Set<ShowcaseCategory> => {
      const next: Set<ShowcaseCategory> = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  return (
    <nav className="lab-sidebar" aria-label="UI Kit navigation">
      <div className="lab-sidebar__brand">
        <span className="lab-sidebar__brand-mark">KU</span>
        <div className="lab-sidebar__brand-text">
          <span className="lab-sidebar__brand-line1">Kuika</span>
          <span className="lab-sidebar__brand-line2">ui-kit · lab</span>
        </div>
      </div>
      <div className="lab-sidebar__counter">
        <span className="lab-sidebar__counter-num">{SHOWCASE_REGISTRY.length}</span>
        <span className="lab-sidebar__counter-label">components indexed</span>
      </div>
      <ul className="lab-sidebar__groups">
        {grouped.map((group: IGroupedEntries): JSX.Element => {
          const isOpen: boolean = !collapsed.has(group.category) || group.category === activeCategory;
          const readyCount: number = group.entries.filter(
            (entry: IShowcaseEntry): boolean => entry.status === "ready"
          ).length;
          return (
            <li
              key={group.category}
              data-category={group.category}
              className={`lab-sidebar__group ${isOpen ? "is-open" : "is-closed"}`}
            >
              <button
                type="button"
                className="lab-sidebar__group-head"
                onClick={() => toggleCategory(group.category)}
                aria-expanded={isOpen}
              >
                <span className="lab-sidebar__group-swatch" aria-hidden="true" />
                <span className="lab-sidebar__group-icon" aria-hidden="true">
                  {isOpen ? "▾" : "▸"}
                </span>
                <span className="lab-sidebar__group-label">{CATEGORY_LABELS[group.category]}</span>
                <span className="lab-sidebar__group-count">
                  {readyCount}/{group.entries.length}
                </span>
              </button>
              {isOpen && (
                <ul className="lab-sidebar__items">
                  {group.entries.map((entry: IShowcaseEntry): JSX.Element => {
                    const isActive: boolean = entry.slug === activeSlug;
                    return (
                      <li key={entry.slug}>
                        <button
                          type="button"
                          className={`lab-sidebar__item ${isActive ? "is-active" : ""} ${
                            entry.status === "todo" ? "is-todo" : ""
                          }`}
                          onClick={() => onSelect(entry.slug)}
                          aria-current={isActive ? "page" : undefined}
                        >
                          <span className="lab-sidebar__item-dot" aria-hidden="true" />
                          <span className="lab-sidebar__item-label">{entry.title}</span>
                          {entry.status === "todo" && <span className="lab-sidebar__item-status">draft</span>}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};
